import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { InputField } from '../../../components/ui/input-field';
import { supabase } from '../../../integrations/supabase/client';

const ResetPassword: React.FC = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isValidSession, setIsValidSession] = useState(false);
    const [checkingSession, setCheckingSession] = useState(true);

    useEffect(() => {
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            setIsValidSession(!!session);
            setCheckingSession(false);
        };

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY') {
                setIsValidSession(true);
                setCheckingSession(false);
            } else if (session) {
                setIsValidSession(true);
                setCheckingSession(false);
            }
        });

        checkSession();

        return () => subscription.unsubscribe();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password.length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        if (password !== confirmPassword) {
            setError('Las contraseñas no coinciden');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const { error: updateError } = await supabase.auth.updateUser({
                password: password
            });

            if (updateError) throw updateError;

            setSuccess(true);
        } catch (err: any) {
            console.error('Error al actualizar contraseña:', err);
            setError(err.message || 'Error al actualizar la contraseña');
        } finally {
            setLoading(false);
        }
    };

    if (checkingSession) {
        return (
            <main className="w-full h-screen flex items-center justify-center bg-neutral-100">
                <div className="text-center">
                    <p className="text-[#2B3A51]">Verificando sesión...</p>
                </div>
            </main>
        );
    }

    if (!isValidSession) {
        return (
            <main className="w-full h-screen flex relative bg-white max-md:flex-col max-md:h-auto">
                <div className="w-6/12 h-full flex flex-col items-center justify-center relative bg-neutral-100 max-md:w-full max-md:min-h-screen max-md:px-5 max-md:py-10 max-sm:p-5">
                    <img
                        src="https://api.builder.io/api/v1/image/assets/TEMP/e4fe78c69a4f9b2dff1a11d2f45f0885adf16022?width=690"
                        alt="Maxi Chatbot Whatsapp AI"
                        className="w-[345px] h-[114px] mb-[60px] max-md:w-[280px] max-md:h-auto max-md:mb-10 max-sm:w-[250px] max-sm:mb-[30px]"
                    />
                    <div className="w-[80%] flex flex-col max-md:w-full text-center">
                        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-6">
                            <h2 className="text-lg font-semibold mb-2">⚠️ Enlace inválido o expirado</h2>
                            <p className="text-sm">
                                El enlace de recuperación no es válido o ha expirado. Por favor, solicita un nuevo enlace.
                            </p>
                        </div>
                        <Button
                            onClick={() => window.location.href = '/password-recovery'}
                            className="rounded-[45px] px-6"
                        >
                            Solicitar nuevo enlace
                        </Button>
                        <button
                            type="button"
                            onClick={() => window.location.href = '/login'}
                            className="text-[#2B3A51] text-center text-sm font-normal bg-transparent border-none cursor-pointer hover:underline mt-4"
                        >
                            Volver al inicio de sesión
                        </button>
                    </div>
                </div>
                <section className="w-6/12 h-full relative overflow-hidden max-md:hidden">
                    <img
                        src="https://api.builder.io/api/v1/image/assets/TEMP/906afa4b45dd2fb35efd3b52d64bc69d76718f6a?width=1742"
                        alt="Background"
                        className="w-full h-full object-cover"
                    />
                </section>
            </main>
        );
    }

    if (success) {
        return (
            <main className="w-full h-screen flex relative bg-white max-md:flex-col max-md:h-auto">
                <div className="w-6/12 h-full flex flex-col items-center justify-center relative bg-neutral-100 max-md:w-full max-md:min-h-screen max-md:px-5 max-md:py-10 max-sm:p-5">
                    <img
                        src="https://api.builder.io/api/v1/image/assets/TEMP/e4fe78c69a4f9b2dff1a11d2f45f0885adf16022?width=690"
                        alt="Maxi Chatbot Whatsapp AI"
                        className="w-[345px] h-[114px] mb-[60px] max-md:w-[280px] max-md:h-auto max-md:mb-10 max-sm:w-[250px] max-sm:mb-[30px]"
                    />
                    <div className="w-[80%] flex flex-col max-md:w-full text-center">
                        <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-lg mb-6">
                            <h2 className="text-lg font-semibold mb-2">✅ Contraseña actualizada</h2>
                            <p className="text-sm">
                                Tu contraseña ha sido actualizada exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
                            </p>
                        </div>
                        <Button
                            onClick={() => window.location.href = '/login'}
                            className="rounded-[45px] px-6"
                        >
                            Ir al inicio de sesión
                        </Button>
                    </div>
                </div>
                <section className="w-6/12 h-full relative overflow-hidden max-md:hidden">
                    <img
                        src="https://api.builder.io/api/v1/image/assets/TEMP/906afa4b45dd2fb35efd3b52d64bc69d76718f6a?width=1742"
                        alt="Background"
                        className="w-full h-full object-cover"
                    />
                </section>
            </main>
        );
    }

    return (
        <main className="w-full h-screen flex relative bg-white max-md:flex-col max-md:h-auto">
            <div className="w-6/12 h-full flex flex-col items-center justify-center relative bg-neutral-100 max-md:w-full max-md:min-h-screen max-md:px-5 max-md:py-10 max-sm:p-5">
                <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/e4fe78c69a4f9b2dff1a11d2f45f0885adf16022?width=690"
                    alt="Maxi Chatbot Whatsapp AI"
                    className="w-[345px] h-[114px] mb-[60px] max-md:w-[280px] max-md:h-auto max-md:mb-10 max-sm:w-[250px] max-sm:mb-[30px]"
                />
                <div className="w-[80%] flex flex-col max-md:w-full max-md:max-w-xs">
                    <h1 className="text-[#2B3A51] text-center text-lg font-bold mb-[30px] max-sm:text-base max-sm:mb-5">
                        Nueva Contraseña
                    </h1>
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                            {error}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="w-full">
                        <div className="space-y-4 mb-5">
                            <InputField
                                label="Nueva Contraseña"
                                placeholder="Mínimo 6 caracteres"
                                name="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <InputField
                                label="Confirmar Contraseña"
                                placeholder="Confirma tu nueva contraseña"
                                name="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                            {confirmPassword && password !== confirmPassword && (
                                <div className="text-red-500 text-sm">
                                    ⚠️ Las contraseñas no coinciden
                                </div>
                            )}
                        </div>
                        <Button
                            type="submit"
                            variant="secondary"
                            className="w-full rounded-[45px]"
                            disabled={loading}
                        >
                            {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
                        </Button>
                    </form>
                    <button
                        type="button"
                        onClick={() => window.location.href = '/login'}
                        className="text-[#2B3A51] text-center text-sm font-normal bg-transparent border-none cursor-pointer hover:underline mt-4"
                    >
                        Volver al inicio de sesión
                    </button>
                </div>
            </div>
            <section className="w-6/12 h-full relative overflow-hidden max-md:hidden">
                <img
                    src="https://api.builder.io/api/v1/image/assets/TEMP/906afa4b45dd2fb35efd3b52d64bc69d76718f6a?width=1742"
                    alt="Background"
                    className="w-full h-full object-cover"
                />
            </section>
        </main>
    );
};

export default ResetPassword;
